import Base from "./Base"
import Organization from "./Organization"
import request from "../services/request"

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000

// IndexedDB doesn't support indexing boolean keys, we thus translate some key
// values to 1 and 0 which are indexable.
function fixBooleanField(obj, key) {
  obj[key] = obj[key] ? 1 : 0
}

function digEstablishment(organizations, key, result = []) {
  organizations.forEach(organization => {
    if (organization.children) {
      digEstablishment(organization.children, key, result)
    } else {
      result.push(organization[key])
    }
  })
  return result
}

class ConsignmentStock extends Base {
  search(filters = {}) {
    return this.promise().then(table => {
      let conditions = {}
      let query
      // simple query builder (EQUAL)
      if (filters.clientId && filters.clientId.match(/^CA\d+$/)) {
        conditions.client_id = filters.clientId
      }
      if (filters.contactName) {
        conditions.contact_name = filters.contactName
      }
      if (filters.monument) {
        conditions["monument.name"] = filters.monument
      }
      if (filters.granite) {
        conditions["monument.granite"] = filters.granite
      }
      switch (filters.delivered) {
        case "shipping":
          conditions.delivered = 0
          break
        case "delivered":
          conditions.delivered = 1
          break
      }
      switch(filters.verified){
          case true:
            conditions.monument_presence_verified = 1
            break
          case false:
            conditions.monument_presence_verified= 0

      }
      if (filters.status) {
        conditions.status = filters.status
      }
      if (filters.placement) {
        conditions.placement = filters.placement
      }

      switch (filters.image) {
        case "none":
          conditions["image.onsite"] = 0
          break
        case "onsite":
        case "outdated":
          conditions["image.onsite"] = 1
          break
      }

      // initial query
      if (Object.keys(conditions).length) {
        query = table.where(conditions)
      } else {
        query = table.toCollection()
      }

      // advanced filters (LIKE, LT, ...)
      if (filters.serialNumber) {
        query = query.and(item => item.serial_number.startsWith(filters.serialNumber))
      }
      if (filters.image === "outdated") {
        const outdated = Date.now() - MILLISECONDS_PER_DAY * 90
        query = query.and(item => item.image.taken_on && (new Date(item.image.taken_on).getTime() < outdated))
      }

      // final filter (list of client_ids)
      if (filters.clientId && !conditions.client_id) {
        return Organization
          .find(filters.clientId)
          .then(organization => {
            const clientIds = digEstablishment(organization.children, "client_id")
            return query.and(item => clientIds.includes(item.client_id)).toArray()
          })
      }
      return query.toArray()
    })
  }

  pluck(field, callback) {
    return this.promise().then(table => {
      let result = new Set()

      return table
        .where(field).notEqual("")
        .each(item => result.add(callback(item)))
        .then(() => Array.from(result))
    })
  }

  // Returns the list of monuments that are available in the current
  // consignment stocks.
  monuments() {
    return this.pluck("monument.name", item => item.monument.name)
  }

  // Returns the list of granites that are available in the current
  // consignment stocks.
  granites() {
    return this.pluck("monument.granite", item => item.monument.granite)
  }

  // Returns the list of organizations that have at least one consignment stock,
  // possibly filtered to a selected contact name.
  clientIds(options = {}) {
    return this.promise().then(table => {
      let result = new Set()
      let query = table.where("client_id").notEqual("")
      if (options.contact_name) {
        query = query.and(item => item.contact_name === options.contact_name)
      }
      return query
        .each(item => result.add(item.client_id))
        .then(() => Array.from(result))
    })
  }

  // Returns the list of contact names that have at least one consignment stock.
  contactNames() {
    return this.pluck("contact_name", item => item.contact_name)
  }

  // Sends a renewal request for a consignment stock to the sales department.
  // May fail if the consignment stock has already been renewed once, or has
  // already been billed.
  renew(id) {
    return request
      .post(`/api/v4/consignment_stocks/${id}/renew`)
      .then(response => this._update(id, response))
  }

  // Sends a billing request for a consignment stock to the sales department.
  // May fail if the consignment stock has already been billed.
  bill(id) {
    return request
      .post(`/api/v4/consignment_stocks/${id}/bill`)
      .then(response => this._update(id, response))
  }

  // Updates some attributes for a consignment stock.
  update(id, data) {
    return request
      .put(`/api/v4/consignment_stocks/${id}`, null, data)
      .then(response => this._update(id, response))
  }

  _update(id, changes) {
    if (changes) {
      const data = this._fixModel(JSON.parse(JSON.stringify(changes)))
      this.promise().then(table => table.update(id, data))
    }
    return changes
  }

  _fixModel(obj) {
    fixBooleanField(obj, "delivered")
    fixBooleanField(obj, "monument_presence_verified")
    fixBooleanField(obj.image, "onsite")
    return obj
  }
}

export default new ConsignmentStock("consignment_stocks")
